/* eslint-disable @typescript-eslint/no-magic-numbers */
import { DecimalPipe } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import * as d3 from 'd3';

import { calculateCommonTickStep, calculateTickValuesForZeroBasedYAxis } from '@shared/helpers/graph/tick-values.helper';
import { D3ChartSvgg, YAxisData } from '@shared/types/common-graph.types';

import { ChartYAxisConfig } from '../chart.view-model';

@Injectable()
export class YAxisService<Data extends object> {
  readonly #decimalPipe = inject(DecimalPipe);

  public createYAxis(
    chartsArea: D3ChartSvgg,
    availableChartHeight: number,
    translateX: number,
    data: Data[],
    config: ChartYAxisConfig<Data>,
    right?: boolean
  ): {
    yScale: YAxisData;
    yAxisWidth?: number;
    yAxisG?: D3ChartSvgg;
  } {
    const min = config.min ?? 0;
    const max =
      config.max ??
      data.reduce((acc, curr, idx) => {
        const value = config.dataKey ? (curr[config.dataKey] as number) : config.dataMapper!(curr, idx);

        return Math.max(acc, value ?? 0);
      }, 0);
    const step = config.step ?? calculateCommonTickStep(max - min);
    const tickValues = config.tickValues ?? calculateTickValuesForZeroBasedYAxis(max, min, step);
    const maxTickValue = config.maxTickValue ?? tickValues[tickValues.length - 1];
    const minTickValue = tickValues[0];
    // A zero-only domain makes D3 place the zero tick in the middle. Reserve scale height above it instead.
    const scaleMaxTickValue = min === 0 && max === 0 ? step : maxTickValue;

    const y = d3.scaleLinear().domain([minTickValue, scaleMaxTickValue]).range([availableChartHeight, 0]);
    const yAxis = d3.axisLeft<number>(y);
    yAxis.tickValues(tickValues);
    yAxis.tickFormat((v) => {
      if (config.tickFormat) {
        return config.tickFormat(v);
      }

      return this.#decimalPipe.transform(v)!;
    });

    const yScale = {
      scale: y,
      axis: yAxis,
      tickValues,
    };

    if (config.hidden) {
      return {
        yScale,
      };
    }

    const yAxisesG = chartsArea.append('g').attr('transform', `translate(${translateX}, 0)`);

    const yAxisG = yAxisesG.append('g').call(yAxis);

    yAxisG.selectAll('.domain').remove();
    yAxisG.selectAll('.tick line').remove();

    const tickTexts = yAxisG.selectAll('.tick text');

    tickTexts.nodes().forEach((node, idx) => {
      const defaultColorClass = 'fill-foreground-primary-60';

      const customColorClass = config.tickClassFn ? config.tickClassFn(tickValues[idx], idx) : undefined;

      const colorClass = customColorClass ?? defaultColorClass;
      d3.select(node).attr('class', `${colorClass} text-xs`);
    });

    tickTexts.attr('x', 0).attr('text-anchor', right ? 'start' : 'end');

    if (config.label) {
      this.addLabelToYAxis(yAxisesG, config.label, right, config.gapBelowLabel);
    }

    const axisWidth = Math.ceil(yAxisesG.node()!.getBBox().width);

    return {
      yScale,
      yAxisWidth: axisWidth,
      yAxisG: yAxisesG,
    };
  }

  private addLabelToYAxis(yAxis: D3ChartSvgg, label: string, right?: boolean, gapBelowLabel?: number): void {
    const classes = 'fill-foreground-primary-60 text-xs font-semibold';

    const text = yAxis.append('g').attr('x', 0).attr('y', 0).append('text').attr('class', classes);

    text
      .append('tspan')
      .attr('x', 0)
      .attr('y', 8)
      .attr('dx', 0)
      .attr('dy', 0)
      .attr('text-anchor', right ? 'start' : 'end')
      .text(label);

    const bBoxOfText = text.node()!.getBBox();
    const heightOfText = Math.ceil(bBoxOfText.height);
    gapBelowLabel = gapBelowLabel ?? 12;
    text.attr('transform', `translate(0, ${-1 * (heightOfText + gapBelowLabel)})`);
  }
}
